/**
 * Wall-clock helpers in IANA timezones, DST-safe, using Intl only (works in workerd, Node and browsers).
 * The Agents SDK cron runs in UTC, so every local-time schedule is computed here and armed as a one-shot Date.
 */

export interface Wall {
  y: number;
  mo: number;
  d: number;
  h: number;
  mi: number;
  s: number;
  /** 0 = Sunday … 6 = Saturday */
  dow: number;
}

const formatters = new Map<string, Intl.DateTimeFormat>();
function formatter(tz: string): Intl.DateTimeFormat {
  let f = formatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hourCycle: "h23",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      weekday: "short",
    });
    formatters.set(tz, f);
  }
  return f;
}

const DOW: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

export function isValidTimezone(tz: string): boolean {
  try {
    formatter(tz).format(0);
    return true;
  } catch {
    return false;
  }
}

export function wall(tz: string, at: number): Wall {
  const parts = formatter(tz).formatToParts(new Date(at));
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "0";
  return { y: +get("year"), mo: +get("month"), d: +get("day"), h: +get("hour") % 24, mi: +get("minute"), s: +get("second"), dow: DOW[get("weekday")] ?? 0 };
}

/** UTC epoch of a local wall-clock time (two passes handle DST edges; non-existent times move forward). */
export function zonedToUtc(tz: string, y: number, mo: number, d: number, h: number, mi: number): number {
  const target = Date.UTC(y, mo - 1, d, h, mi);
  let t = target;
  for (let i = 0; i < 2; i++) {
    const w = wall(tz, t);
    const asUtc = Date.UTC(w.y, w.mo - 1, w.d, w.h, w.mi, w.s);
    const offset = asUtc - Math.floor(t / 1000) * 1000;
    t = target - offset;
  }
  return t;
}

export function parseHm(hm: string): { h: number; m: number } {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hm.trim());
  if (!m) throw new RangeError(`invalid HH:MM: ${hm}`);
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (h > 23 || mi > 59) throw new RangeError(`invalid HH:MM: ${hm}`);
  return { h, m: mi };
}

export function isValidHm(hm: string): boolean {
  try {
    parseHm(hm);
    return true;
  } catch {
    return false;
  }
}

/** UTC instant of local HH:MM on the given local date; a time inside a DST gap maps to the first valid instant after it. */
function instantOn(tz: string, y: number, mo: number, d: number, h: number, m: number): number {
  let t = zonedToUtc(tz, y, mo, d, h, m);
  for (let i = 0; i < 4; i++) {
    const w = wall(tz, t);
    const sameDay = w.y === y && w.mo === mo && w.d === d;
    const wallMin = w.h * 60 + w.mi;
    if (sameDay && wallMin >= h * 60 + m) return t;
    if (!sameDay && (w.y > y || (w.y === y && (w.mo > mo || (w.mo === mo && w.d > d))))) return t; // already past the gap into the next day
    t += 30 * 60_000; // inside a DST gap (or rounding): step forward to the first valid wall time
  }
  return t;
}

/** Next occurrence (strictly after `now` + 1 s) of local HH:MM in `tz`, DST-safe. */
export function nextLocal(tz: string, hm: string, now: number = Date.now()): number {
  const { h, m } = parseHm(hm);
  const w = wall(tz, now);
  let base = Date.UTC(w.y, w.mo - 1, w.d);
  for (let day = 0; day < 4; day++) {
    const dt = new Date(base + day * 86_400_000);
    const t = instantOn(tz, dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate(), h, m);
    if (t > now + 1000) return t;
  }
  base += 86_400_000;
  return instantOn(tz, new Date(base).getUTCFullYear(), new Date(base).getUTCMonth() + 1, new Date(base).getUTCDate(), h, m);
}

/** UTC instant of a local "YYYY-MM-DDTHH:mm" wall time in `tz` (DST gaps move forward). */
export function localDateTimeToUtc(tz: string, local: string): number {
  const mt = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})$/.exec(local.trim());
  if (!mt) throw new RangeError(`invalid local datetime: ${local}`);
  return instantOn(tz, +mt[1]!, +mt[2]!, +mt[3]!, +mt[4]!, +mt[5]!);
}

/** Local calendar date "YYYY-MM-DD". */
export function localDate(tz: string, at: number = Date.now()): string {
  const w = wall(tz, at);
  return `${w.y}-${String(w.mo).padStart(2, "0")}-${String(w.d).padStart(2, "0")}`;
}

export function minutesOfDay(tz: string, at: number = Date.now()): number {
  const w = wall(tz, at);
  return w.h * 60 + w.mi;
}

/** Is local time inside [start, end)? Windows may wrap midnight (e.g. 22:00–07:30). Equal bounds = never. */
export function inWindow(tz: string, start: string, end: string, at: number = Date.now()): boolean {
  const s = parseHm(start);
  const e = parseHm(end);
  const a = s.h * 60 + s.m;
  const b = e.h * 60 + e.m;
  const x = minutesOfDay(tz, at);
  if (a === b) return false;
  return a < b ? x >= a && x < b : x >= a || x < b;
}

/** Epoch of the end of the window that contains `at` (or `at` if not inside). */
export function windowEnd(tz: string, start: string, end: string, at: number = Date.now()): number {
  if (!inWindow(tz, start, end, at)) return at;
  return nextLocal(tz, end, at);
}

export function isWeekend(tz: string, at: number = Date.now()): boolean {
  const dow = wall(tz, at).dow;
  return dow === 0 || dow === 6;
}

/** "mardi 29 octobre, 08:12" in French for prompts and UI. */
export function formatLocalFr(tz: string, at: number = Date.now()): string {
  return new Intl.DateTimeFormat("fr-FR", { timeZone: tz, weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }).format(new Date(at));
}

/** "Tuesday, October 29 at 8:12 AM": the same moment for the fixed texts of an English-speaking user. */
export function formatLocalEn(tz: string, at: number = Date.now()): string {
  return new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "long", day: "numeric", month: "long", hour: "numeric", minute: "2-digit" }).format(new Date(at));
}

export function formatIsoLocal(tz: string, at: number = Date.now()): string {
  const w = wall(tz, at);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${w.y}-${p(w.mo)}-${p(w.d)}T${p(w.h)}:${p(w.mi)}`;
}
